import React, { useState } from "react";
import styles from "../DataInput.module.scss";
import { DraggerConstants, FileUploadConstants } from "../constants";
import UploadIcon from "../../../../assets/images/upload-icon.svg";
import PdfIcon from "../../../../assets/images/view_pdf.svg";
import { Storage } from "aws-amplify";
import { Upload, message, Modal } from "antd";
import type { UploadProps } from "antd";

export interface ReplaceTicketDraggerProps {
  onFilesUploaded?: () => {};
  docType?: string;
  filesUploaded?: [];
  maxFileCount?: string;
  allowedFileTypes?: string;
  setDocumentKey?: () => {};
  clearReplaceTktError?: () => {};
}

const ReplaceTicketDragger = (props: ReplaceTicketDraggerProps) => {
  const {
    onFilesUploaded,
    docType,
    filesUploaded,
    maxFileCount,
    allowedFileTypes,
    setDocumentKey,
    clearReplaceTktError,
  } = props;
  const { Dragger } = Upload;
  const {
    UPLOAD_PRIMARY_TXT,
    UPLOAD_HINT,
    MAX_FILE_COUNT,
    DEFAULT_SHEET_STATE,
    FILE_UPLOAD_SUCCESS_TXT,
    PDF_ICON_ALT,
    UPLOAD_ICON_ALT,
  } = DraggerConstants;
  const {
    DUPLICATE_WARNING,
    UPLOAD_SUCCESS,
    UPLOAD_FAILED,
    DUPLICATE_TITLE,
    FILE_SIZE_WARNING,
    FILE_TYPE_WARNING_2,
    FILE_TYPE_WARNING,
    MAX_COUNT_WARNING,
  } = FileUploadConstants;

  const [fileList, setFileList] = useState(
    filesUploaded?.length ? filesUploaded : []
  );

  const handleUpload = async (file, onSuccess, onError) => {
    try {
      const timestampMs = new Date().getTime();
      const fileName = `${timestampMs}/${file.name}`;
      const result = await Storage.put(fileName, file, {
        contentType: file.type,
      });
      const fileURL = await Storage.get(result.key, { level: "public" });
      setDocumentKey?.(result.key);
      const newFile = {
        name: file.name,
        docAttachment: fileURL?.split("?")?.[0],
        docAttachmentId: file.uid,
        fileType: file.type,
        sheetStatus: DEFAULT_SHEET_STATE,
        docType: docType,
        document: docType,
        signatureVerified: false,
      };
      message.success(UPLOAD_SUCCESS(file.name));
      onSuccess(FILE_UPLOAD_SUCCESS_TXT);
      onFilesUploaded(newFile);
    } catch (error) {
      message.error(UPLOAD_FAILED(file.name));
      onError(error);
    }
  };

  const maxCount = maxFileCount ? maxFileCount : MAX_FILE_COUNT;
  const uploadProps: UploadProps = {
    fileList,
    maxCount: maxCount, //Only one file,
    beforeUpload: (file) => {
      const isDuplicate = fileList.some(
        (existing) => existing.name === file.name
      );

      if (isDuplicate) {
        Modal.confirm({
          title: DUPLICATE_TITLE,
          content: DUPLICATE_WARNING(file.name),
          onOk: () => {
            uploadProps.customRequest?.({
              file,
              onSuccess: () => message.success(UPLOAD_SUCCESS(file.name)),
              onError: (err) => message.error(UPLOAD_FAILED(file.name)),
              onProgress: () => {},
            });
          },
        });
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= maxCount) {
        message.error(MAX_COUNT_WARNING(maxCount));
        return Upload.LIST_IGNORE;
      }

      if (!allowedFileTypes && file.type !== FileTypes.PDF) {
        message.error(FILE_TYPE_WARNING);
        return Upload.LIST_IGNORE;
      }

      if (allowedFileTypes && !allowedFileTypes.includes(file.type)) {
        message.error(FILE_TYPE_WARNING_2);
        return Upload.LIST_IGNORE;
      }

      if (file.size > 5 * 1024 * 1024) {
        message.error(FILE_SIZE_WARNING);
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    headers: {
      authorization: "authorization-text",
    },
    customRequest: ({ file, onSuccess, onError }) =>
      handleUpload(file, onSuccess, onError),
    onChange(info) {
      setFileList(info.fileList);
    },
    onRemove: async (file) => {
      setDocumentKey(undefined);
      setFileList([]);
      onFilesUploaded([]);
      clearReplaceTktError();
    },
    iconRender: (file) => (
      <img src={PdfIcon} alt={PDF_ICON_ALT} className={styles.pdfIconStyle} />
    ),
    progress: { style: { marginLeft: 16 } },
  };

  return (
    <div>
      <Dragger {...uploadProps}>
        <p className={styles.UploadIconContainer}>
          <img src={UploadIcon} alt={UPLOAD_ICON_ALT} />
        </p>
        <p className="ant-upload-text">{UPLOAD_PRIMARY_TXT}</p>
        <p className="ant-upload-hint">{UPLOAD_HINT}</p>
      </Dragger>
    </div>
  );
};

export default ReplaceTicketDragger;
